import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { Layout } from "@/components/layout";
import CombinedDashboard from "@/pages/combined-dashboard";
import Customers from "@/pages/customers/index";
import CustomerDetail from "@/pages/customers/detail";
import ProjectsListPage from "@/pages/projects/index";
import ProjectDetailPage from "@/pages/projects/detail";
import CalendarPage from "@/pages/calendar/index";
import Estimates from "@/pages/estimates/index";
import NewEstimate from "@/pages/estimates/new";
import EstimateDetail from "@/pages/estimates/detail";
import SettingsPage from "@/pages/settings";
import ProductsPage from "@/pages/products/index";
import ProductDetail from "@/pages/products/detail";
import CostCatalogPage from "@/pages/cost-catalog/index";
import CostCatalogDetail from "@/pages/cost-catalog/detail";
import ImportPage from "@/pages/import/index";
import IntegrationsPage from "@/pages/integrations";
import QuickBooksSettingsPage from "@/pages/integrations/quickbooks";
import XeroSettingsPage from "@/pages/integrations/xero";
import InvoiceList from "@/pages/invoices/index";
import NewInvoice from "@/pages/invoices/new";
import InvoiceDetail from "@/pages/invoices/detail";
import UpgradePage from "@/pages/upgrade";
import BillingSuccessPage from "@/pages/billing-success";
import BillingCancelledPage from "@/pages/billing-cancelled";
import PaymentSuccessPage from "@/pages/payment-success";
import PaymentCancelledPage from "@/pages/payment-cancelled";
import AuthPage from "@/pages/auth";
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";
import ProSignupPage from "@/pages/signup-pro";
import VerifyEmailPage from "@/pages/verify-email";
import TemplatesPage from "@/pages/templates/index";
import SupportPage from "@/pages/support";
import ActivityPage from "@/pages/activity";
import FieldIndexPage from "@/pages/field/index";
import FieldProjectPage from "@/pages/field/project";
import FieldPhotoDetail from "@/pages/field/photo-detail";
import FieldSharePage from "@/pages/field/share";
import FieldSettingsPage from "@/pages/field/settings";
import FieldToolsPage from "@/pages/field/tools";
import TeamSettingsPage from "@/pages/settings/team";
import { AdminLayout } from "@/pages/admin/layout";
import AdminAnalytics from "@/pages/admin/analytics";
import AdminUsers from "@/pages/admin/users";
import AdminSettings from "@/pages/admin/settings";
import AdminBroadcasts from "@/pages/admin/broadcasts";
import AdminSupport from "@/pages/admin/support";
import AdminUserDetail from "@/pages/admin/user-detail";
import AdminBlog from "@/pages/admin/blog";
import AdminBlogEditor from "@/pages/admin/blog-editor";
import AdminPublicAnalytics from "@/pages/admin/public-analytics";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { SupportNotificationsProvider } from "@/hooks/use-support-notifications";
import { AdminSupportNotificationsProvider } from "@/hooks/use-admin-support-notifications";
import { SetupGuideProvider } from "@/hooks/use-setup-guide";
import { SetupGuide } from "@/components/SetupGuide";
import { WelcomeSetup } from "@/components/WelcomeSetup";
import { GuidedTourOverlay } from "@/components/GuidedTourOverlay";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { pushEvent } from "@/lib/gtm";
import { usePageTracker } from "@/hooks/use-page-tracker";
import { useHeartbeat } from "@/hooks/use-heartbeat";
import { useScrollToTop } from "@/hooks/use-scroll-to-top";
import { useRedditPixel } from "@/hooks/use-reddit-pixel";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { DuplicateTabBanner } from "@/components/DuplicateTabBanner";
import { PendingFreeBuilderImport } from "@/components/pending-free-builder-import";
import { IdleLockGate } from "@/components/IdleLockGate";
import { useFieldMode } from "@/hooks/use-field-mode";

const queryClient = new QueryClient();

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function AdminRoutes() {
  const { isAdmin, adminChecked } = useAdminAuth();

  if (!adminChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Redirect to="/" />;
  }

  return (
    <AdminSupportNotificationsProvider>
      <AdminLayout>
        <Switch>
          <Route path="/admin/analytics/public" component={AdminPublicAnalytics} />
          <Route path="/admin/users" component={AdminUsers} />
          <Route path="/admin/support" component={AdminSupport} />
          <Route path="/admin/email" component={AdminBroadcasts} />
          <Route path="/admin/settings" component={AdminSettings} />
          <Route path="/admin" component={AdminAnalytics} />
          <Route component={NotFound} />
        </Switch>
      </AdminLayout>
    </AdminSupportNotificationsProvider>
  );
}

function AdminBlogPage() {
  const { isAdmin, adminChecked } = useAdminAuth();

  if (!adminChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Redirect to="/" />;
  }

  return (
    <AdminSupportNotificationsProvider>
      <AdminLayout>
        <AdminBlog />
      </AdminLayout>
    </AdminSupportNotificationsProvider>
  );
}

function AdminBlogEditorPage() {
  const { isAdmin, adminChecked } = useAdminAuth();

  if (!adminChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Redirect to="/" />;
  }

  return (
    <AdminSupportNotificationsProvider>
      <AdminLayout>
        <AdminBlogEditor />
      </AdminLayout>
    </AdminSupportNotificationsProvider>
  );
}

function AdminUserDetailPage() {
  const { isAdmin, adminChecked } = useAdminAuth();

  if (!adminChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Redirect to="/" />;
  }

  return (
    <AdminSupportNotificationsProvider>
      <AdminLayout>
        <AdminUserDetail />
      </AdminLayout>
    </AdminSupportNotificationsProvider>
  );
}

function FieldWorkerRoutes() {
  const { user } = useAuth();
  const [location] = useLocation();

  // Force password change on first login before allowing access to anything else.
  if (user?.mustChangePassword && location !== "/field/settings") {
    return <Redirect to="/field/settings" />;
  }

  return (
    <Layout>
      <Switch>
        <Route path="/field/settings" component={FieldSettingsPage} />
        <Route path="/field/tools" component={FieldToolsPage} />
        <Route path="/field" component={FieldIndexPage} />
        <Route path="/field/photo/:id" component={FieldPhotoDetail} />
        <Route path="/field/:id" component={FieldProjectPage} />
        <Route>{() => <Redirect to="/field" />}</Route>
      </Switch>
    </Layout>
  );
}

function AuthenticatedRoutes() {
  const { user } = useAuth();
  const [location] = useLocation();
  const { fieldMode } = useFieldMode();

  // Field workers are locked to the /field section.
  // The explicit location check is synchronous (unlike a rendered <Redirect>
  // which queues navigation in a useEffect), so field workers never briefly
  // see owner routes when returning from idle or re-authenticating.
  if (user?.role === "field") {
    if (!location.startsWith("/field")) {
      return <Redirect to="/field" />;
    }
    return <FieldWorkerRoutes />;
  }

  return (
    <Layout>
      <Switch>
        <Route path="/verify-email" component={VerifyEmailPage} />
        <Route path="/">{() => <Redirect to={fieldMode ? "/field" : "/dashboard"} />}</Route>
        <Route path="/dashboard">{() => <CombinedDashboard />}</Route>
        <Route path="/customers" component={Customers} />
        <Route path="/customers/:id" component={CustomerDetail} />
        <Route path="/projects" component={ProjectsListPage} />
        <Route path="/projects/:id" component={ProjectDetailPage} />
        <Route path="/calendar" component={CalendarPage} />
        <Route path="/quotes" component={Estimates} />
        <Route path="/quotes/new" component={NewEstimate} />
        <Route path="/quotes/:id" component={EstimateDetail} />
        <Route path="/products" component={ProductsPage} />
        <Route path="/products/:id" component={ProductDetail} />
        <Route path="/cost-catalog" component={CostCatalogPage} />
        <Route path="/cost-catalog/:id" component={CostCatalogDetail} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/settings/team" component={TeamSettingsPage} />
        <Route path="/import" component={ImportPage} />
        <Route path="/integrations" component={IntegrationsPage} />
        <Route path="/integrations/quickbooks" component={QuickBooksSettingsPage} />
        <Route path="/integrations/xero" component={XeroSettingsPage} />
        {/* The setup wizard was removed in task #944 — redirect bookmarked URLs. */}
        <Route path="/integrations/quickbooks/setup">{() => <Redirect to="/integrations" />}</Route>
        <Route path="/invoices">{() => <InvoiceList />}</Route>
        <Route path="/invoices/list">{() => <Redirect to="/invoices" />}</Route>
        <Route path="/invoices/new" component={NewInvoice} />
        <Route path="/invoices/:id">{() => <InvoiceDetail />}</Route>
        <Route path="/templates" component={TemplatesPage} />
        <Route path="/support" component={SupportPage} />
        <Route path="/activity" component={ActivityPage} />
        <Route path="/upgrade" component={UpgradePage} />
        <Route path="/field" component={FieldIndexPage} />
        <Route path="/field/tools" component={FieldToolsPage} />
        <Route path="/field/photo/:id" component={FieldPhotoDetail} />
        <Route path="/field/:id" component={FieldProjectPage} />
        <Route path="/get-started">{() => <Redirect to={fieldMode ? "/field" : "/dashboard"} />}</Route>
        <Route path="/auth">{() => <Redirect to={fieldMode ? "/field" : "/dashboard"} />}</Route>
        <Route path="/login">{() => <Redirect to={fieldMode ? "/field" : "/dashboard"} />}</Route>
        <Route path="/signup">{() => <Redirect to={fieldMode ? "/field" : "/dashboard"} />}</Route>
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

function LoginPage() {
  return <AuthPage initialMode="login" />;
}

function SignupPage() {
  return <AuthPage initialMode="signup" />;
}

function stripTrailingSlash(path: string): string {
  if (path.length > 1 && path.endsWith("/")) {
    return path.replace(/\/+$/, "");
  }
  return path;
}

function UnauthenticatedRoutes({ location }: { location: string }) {
  return (
    <Switch location={location}>
      <Route path="/auth">{() => <AuthPage />}</Route>
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/verify-email" component={VerifyEmailPage} />
      <Route>{() => <Redirect to="/login" />}</Route>
    </Switch>
  );
}

function useGoogleSignupEvent() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const signup = params.get("signup");
    const eventId = params.get("signup_event_id");
    if (signup === "google" && eventId) {
      pushEvent("sign_up", { method: "google", event_id: eventId });
      params.delete("signup");
      params.delete("signup_event_id");
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
      history.replaceState(null, "", newUrl);
    }
  }, []);
}

function AppRouter() {
  const { user, loading, notifySignedOut } = useAuth();
  const [rawLocation] = useLocation();
  const location = stripTrailingSlash(rawLocation);
  usePageTracker();
  useScrollToTop();
  useHeartbeat(!!user, notifySignedOut);
  useRedditPixel();
  useGoogleSignupEvent();

  // Public field share page — no auth required.
  // Must be wrapped in a Route so wouter's useParams() can extract :token.
  if (location.startsWith("/field/share/")) {
    return (
      <Switch>
        <Route path="/field/share/:token" component={FieldSharePage} />
      </Switch>
    );
  }

  if (location.startsWith("/billing/success")) {
    return <BillingSuccessPage />;
  }

  if (location.startsWith("/billing/cancelled")) {
    return <BillingCancelledPage />;
  }

  if (location.startsWith("/payment/success")) {
    return <PaymentSuccessPage />;
  }

  if (location.startsWith("/payment/cancelled")) {
    return <PaymentCancelledPage />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (location.startsWith("/signup/pro")) {
    return <ProSignupPage />;
  }

  if (location === "/forgot-password") {
    return <ForgotPasswordPage />;
  }

  if (location.startsWith("/reset-password")) {
    return <ResetPasswordPage />;
  }

  if (!user) {
    return <UnauthenticatedRoutes location={location} />;
  }

  const AUTH_ROUTES = ["/auth", "/login", "/signup", "/get-started"];
  if (user.isAdmin && AUTH_ROUTES.includes(location)) {
    return <Redirect to="/" />;
  }

  return (
    <IdleLockGate>
      <DuplicateTabBanner />
      <PendingFreeBuilderImport />
      <Switch>
        <Route path="/admin/users/:id" component={AdminUserDetailPage} />
        <Route path="/admin/blog/new" component={AdminBlogEditorPage} />
        <Route path="/admin/blog/:id" component={AdminBlogEditorPage} />
        <Route path="/admin/blog" component={AdminBlogPage} />
        <Route path="/admin"><AdminRoutes /></Route>
        <Route path="/admin/*"><AdminRoutes /></Route>
        <Route><AuthenticatedRoutes /></Route>
      </Switch>
    </IdleLockGate>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <AuthProvider>
            <SetupGuideProvider>
              <SupportNotificationsProvider>
                <AppRouter />
                <SetupGuide />
                <WelcomeSetup />
                <GuidedTourOverlay />
              </SupportNotificationsProvider>
            </SetupGuideProvider>
          </AuthProvider>
        </WouterRouter>
        <CookieConsentBanner />
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
